import { connect } from 'react-redux';
import { flow } from 'lodash';

import { selectData } from './ListView.selectors';
import container from 'src/ServiceContainer';
import {
  cleanUp,
  receiveError as receiveListViewError,
  requestConfig,
  receiveConfig,
  receiveSomeListViewConfig,
} from './ListView.slice';
import ListGridPair from 'src/components/ListGridPair/ListGridPair';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { makePrintSensitive } from 'src/components/higherOrder/Print/PrintSenstive';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { getLocalConfig } from 'src/components/ViewConfiguratorModal/ViewConfiguratorModal.utils';
import { makePopoverSensitive } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { FavoriteListItemStorage } from 'src/components/Subheader/Favorites/Favorites.types';
import { AppThunkDispatch } from 'src/store';
import type { HistoryGridOwnProps as ListViewOwnProps } from 'configuration-schemas/ownProps';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { CollapsibleCompanion } from 'src/common-ui/components/CompanionListView/CompanionListView';
import { toggleCompanionCollapse } from 'src/state/uipose/UiPose.slice';
import { bindActionCreators } from '@reduxjs/toolkit';
import { updateCompanionSortField, updateCompanionSortDirection } from 'src/components/Subheader/Subheader.slice';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import { zNestedGridViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { zCompanionViewDefn, zRollupDefn, zSortByConfig } from 'configuration-schemas/viewdefns/general';

export interface DispatchProps extends Pick<CollapsibleCompanion, 'onToggleCompanion'> {
  onShowView(): void;
  onDestroy(): void;
  onRefetchData(): void;
  onUpdate?(): void;
  onUpdateConfig?(config: TenantConfigViewData): void;
  updateCompanionSortField(field: string): void;
  updateCompanionSortDirection(dir: SortByDirection): void;
}

// TODO: once remaining ListGridPair methods no longer rely on onUpdate/onRefetch/onDestroy
// in DispatchProps type, remove from here
function dispatchToProps(dispatch: AppThunkDispatch, ownProps: ListViewOwnProps): DispatchProps {
  const { defns } = ownProps;
  return {
    ...bindActionCreators({ onToggleCompanion: toggleCompanionCollapse }, dispatch),
    onShowView() {
      dispatch(requestConfig());
      container.tenantConfigClient
        .getTenantViewDefnsWithFavorites({
          defnIds: defns.view,
          appName: ASSORTMENT,
          validationSchemas: [zNestedGridViewDefn, zSortByConfig, zCompanionViewDefn, zRollupDefn],
        })
        .then((config) => {
          const unmodifiedViewDefn = config[0];
          const localConfig: FavoriteListItemStorage | undefined = getLocalConfig(
            defns.view[0],
            (config as any)[defns.view.length],
            dispatch,
            unmodifiedViewDefn,
          );
          if (localConfig && localConfig.config) {
            config[0] = localConfig.config;
          }
          dispatch(
            receiveConfig({
              grid: config[0],
              listSort: config[1],
              list: config[2],
              subheaderRollUp: config[3],
              unmodifiedViewDefn,
            }),
          );
        })
        .catch((error) => {
          dispatch(
            receiveListViewError({
              type: ComponentErrorType.config,
              message: (error as Error)?.message,
              name: ConfDefnComponentType.listView,
              issues: error,
              defnId: error.defnId,
            }),
          );
        });
    },
    onUpdate() {
      // data refetch handled in epic
    },
    onRefetchData() {
      // data refetch handled in epic
    },
    onUpdateConfig(config: TenantConfigViewData) {
      dispatch(receiveSomeListViewConfig(config));
    },
    onDestroy() {
      dispatch(cleanUp());
    },
    updateCompanionSortField(field: string) {
      dispatch(updateCompanionSortField(field));
    },
    updateCompanionSortDirection(dir: SortByDirection) {
      dispatch(updateCompanionSortDirection(dir));
    },
  };
}

const wrappedView = flow(() => ListGridPair, makePopoverSensitive, makePrintSensitive)();

export default connect(selectData, dispatchToProps)(wrappedView);
